import { useState } from 'react';
import { format } from 'date-fns';
import { reportsApi } from '@/services/api';
import { downloadCsv } from '@/utils/csv';
import PurchaseReportFilters from '../shared/PurchaseReportFilters';
import ReportDataTable, { columnsToCsvHeaders, rowsToCsvData } from '../shared/ReportDataTable';
import ReportLayout from '../shared/ReportLayout';
import ReportSummaryCards from '../shared/ReportSummaryCards';
import { fetchPurchaseReportExport, usePurchaseReport } from '../shared/usePurchaseReport';
import { AGING_LABELS, outstandingColumns, OutstandingRow } from './columns';
import { formatMoney } from '@/utils/currency';

export default function OutstandingPayablesPage() {
  const [supplier, setSupplier] = useState('');
  const [supplierLabel, setSupplierLabel] = useState('');
  const [asOnDate, setAsOnDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const params = {
    supplier: supplier || undefined,
    as_on_date: asOnDate,
  };

  const { rows, total, page, setPage, pageSize, summary, isLoading } = usePurchaseReport({
    queryKey: 'outstandingPayables',
    params,
    fetcher: (p) => reportsApi.getOutstandingPayables(p),
  });

  const aging = (summary.aging || {}) as Record<string, number>;

  const handleExport = async () => {
    const data = await fetchPurchaseReportExport(reportsApi.getOutstandingPayables, params);
    downloadCsv(
      `outstanding-payables-${asOnDate}.csv`,
      columnsToCsvHeaders(outstandingColumns),
      rowsToCsvData(data.rows as unknown as OutstandingRow[], outstandingColumns),
    );
  };

  return (
    <ReportLayout
      onExport={handleExport}
      exportDisabled={!total}
      isLoading={isLoading}
      isEmpty={!rows.length}
      page={page}
      total={total}
      pageSize={pageSize}
      onPageChange={setPage}
      filters={
        <PurchaseReportFilters
          dateRangeHook={{
            dateRange: 'Custom Date',
            setDateRange: () => {},
            customFrom: '',
            setCustomFrom: () => {},
            customTo: '',
            setCustomTo: () => {},
            from_date: '',
            to_date: '',
          }}
          supplier={supplier}
          supplierLabel={supplierLabel}
          onSupplierChange={(id, label) => {
            setSupplier(id);
            setSupplierLabel(label);
          }}
          asOnDate={asOnDate}
          onAsOnDateChange={setAsOnDate}
          showAsOnDate
        />
      }
      summary={
        <div className="space-y-4">
          <ReportSummaryCards
            items={[
              {
                label: 'Total Outstanding',
                value: `${formatMoney(summary.total_outstanding as number)}`,
                tone: 'warning',
              },
              { label: 'Total Suppliers', value: Number(summary.total_suppliers ?? 0) },
              {
                label: 'Overdue Amount',
                value: `${formatMoney(summary.overdue_amount as number)}`,
                tone: 'danger',
              },
            ]}
            columns={3}
          />
          <ReportSummaryCards
            items={Object.entries(AGING_LABELS).map(([key, label]) => ({
              label,
              value: `${formatMoney(aging[key])}`,
            }))}
            columns={3}
          />
        </div>
      }
    >
      <ReportDataTable data={rows as never[]} columns={outstandingColumns} />
    </ReportLayout>
  );
}
